'use client';

/**
 * EmptyState Component
 *
 * Displays when no data is available in the table.
 *
 * Typography uses inline styles to avoid Tailwind arbitrary value conflicts.
 * See tokens.ts for detailed explanation of the Tailwind CSS variable issue.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Inbox, LucideIcon } from 'lucide-react';
import { defaultTheme, getTextStyles } from './theme/tokens';

interface EmptyStateProps {
  icon?: LucideIcon;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  colSpan?: number;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon = Inbox,
  title = 'No data available',
  description = 'There are no items to display',
  actionLabel,
  onAction,
  colSpan,
}) => {
  // Get typography styles (inline styles to avoid Tailwind conflicts)
  const textStyles = getTextStyles(defaultTheme);

  return (
    <tbody>
      <tr>
        <td colSpan={colSpan} className="px-6 py-16">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 mb-4">
              <Icon className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="mb-2" style={textStyles.primary()}>
              {title}
            </h3>
            <p className="mb-4 max-w-sm" style={textStyles.secondary()}>
              {description}
            </p>
            {actionLabel && onAction && (
              <Button onClick={onAction} size="sm">
                {actionLabel}
              </Button>
            )}
          </div>
        </td>
      </tr>
    </tbody>
  );
};
